import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import type { CreateMessageInput, Message } from '@/domain/message.types'
import { translationGroupKey, useCreateMessage, useUpdateMessage } from '@/hooks/useMessages'
import type { MatrixCell, MatrixRow } from './matrix-model'
import { isCellInFlight } from './matrix-model'

/** A row's in-progress edits, locale → text. Absent locales are untouched. */
export type MatrixRowDraft = Record<string, string>

/** A new hand-authored locale row for a group — the same shape the detail page's add-locale uses, with text supplied up front. */
function buildLocaleRowInput(row: MatrixRow, source: Message, locale: string, content: string): CreateMessageInput {
	return {
		title: source.title,
		content,
		status: source.status,
		languageIso: locale,
		messageType: source.messageType,
		position: source.position ?? undefined,
		translationGroupId: row.translationGroupId,
		isSource: false,
	}
}

const seedDraft = (row: MatrixRow): MatrixRowDraft =>
	Object.fromEntries(row.cells.map((cell) => [cell.locale, cell.message?.content ?? '']))

/** One cell's pending write, and the locales the draft blanks out - refused as a set before anything is written. */
interface RowWritePlan {
	writes: { cell: MatrixCell; content: string }[]
	clearedLocales: string[]
}

/**
 * What a save would actually write. Every cell is diffed against `baseline` - the
 * row as it read when editing opened - never against the row as it reads now: the
 * group keeps refetching under the open draft as translations land, so diffing
 * against live text would see a cell the user never touched as changed and
 * overwrite the freshly arrived translation with the empty string the draft holds.
 */
function planRowWrites(row: MatrixRow, draft: MatrixRowDraft, baseline: MatrixRowDraft): RowWritePlan {
	const plan: RowWritePlan = { writes: [], clearedLocales: [] }
	for (const cell of row.cells) {
		const next = draft[cell.locale]
		if (next === undefined || next === (baseline[cell.locale] ?? '') || isCellInFlight(cell)) continue
		if (next.trim()) plan.writes.push({ cell, content: next })
		// An emptied existing cell would PUT `content: ''`, leaving a real message
		// row holding no copy: it stays in the translation group, keeps its history,
		// and can never be typed back into, since the create branch is unreachable
		// for a locale that already has a row.
		else if (cell.message) plan.clearedLocales.push(cell.locale)
	}
	return plan
}

const clearedText = (locales: readonly string[]): string =>
	`Emptying a locale does not remove it (${locales.join(', ')}). Restore the text, or delete that locale's message from its own page.`

/** The mutations a save runs through, passed in so the plan stays independent of how the writes are issued. */
interface RowWriters {
	update: (messageId: string, content: string) => Promise<unknown>
	create: (body: CreateMessageInput) => Promise<unknown>
	invalidateGroup: (translationGroupId: string) => Promise<void>
}

/** Issues one plan: every write in parallel, then one re-read of the group they landed in. */
async function runRowWrites(row: MatrixRow, source: Message, plan: RowWritePlan, writers: RowWriters): Promise<void> {
	await Promise.all(
		plan.writes.map(({ cell, content }) =>
			cell.message
				? writers.update(cell.message.messageId, content)
				: writers.create(buildLocaleRowInput(row, source, cell.locale, content)),
		),
	)
	await writers.invalidateGroup(row.translationGroupId)
}

/** The three calls a save issues, bound to the shared mutation observers and cache. */
function useRowWriters(): RowWriters {
	const queryClient = useQueryClient()
	const update = useUpdateMessage()
	const create = useCreateMessage()
	return {
		update: (messageId, content) => update.mutateAsync({ id: messageId, patch: { content } }),
		create: (body) => create.mutateAsync(body),
		invalidateGroup: (groupId) => queryClient.invalidateQueries({ queryKey: translationGroupKey(groupId) }),
	}
}

/** The open row's text: what it read when editing opened, and what the user has typed since. */
function useRowDraft() {
	const [editingRowId, setEditingRowId] = useState<string | null>(null)
	const [draft, setDraft] = useState<MatrixRowDraft>({})
	const [baseline, setBaseline] = useState<MatrixRowDraft>({})

	return {
		editingRowId,
		draft,
		baseline,
		open: (row: MatrixRow) => {
			const snapshot = seedDraft(row)
			setEditingRowId(row.sourceMessageId)
			setDraft(snapshot)
			setBaseline(snapshot)
		},
		close: () => {
			setEditingRowId(null)
			setDraft({})
			setBaseline({})
		},
		setCell: (locale: string, content: string) => setDraft((prev) => ({ ...prev, [locale]: content })),
	}
}

/**
 * In-place row editing for the resolution matrix. One row at a time: opening a
 * second row would leave unsaved text behind with nothing on screen to say so.
 *
 * Saving writes only what actually changed — an update per edited existing cell,
 * a create per newly-filled empty cell — then invalidates the row's group so the
 * new cell state is re-read rather than guessed at locally.
 */
export function useMatrixRowEdit() {
	const writers = useRowWriters()
	const editor = useRowDraft()
	// Spans the whole save rather than reading the two mutations' own pending flags:
	// one observer tracks only its most recent call, so either flag can fall back to
	// false while the rest of a multi-cell save is still in flight - long enough for
	// a second press of Save to reissue every write.
	const [isSaving, setIsSaving] = useState(false)
	const [saveError, setSaveError] = useState<string | null>(null)

	const startEdit = (row: MatrixRow) => {
		setSaveError(null)
		editor.open(row)
	}

	const cancelEdit = () => {
		editor.close()
		setSaveError(null)
	}

	const saveEdit = async (row: MatrixRow) => {
		if (isSaving) return
		const source = row.cells.find((cell) => cell.isSourceLocale)?.message
		if (!source) return
		const plan = planRowWrites(row, editor.draft, editor.baseline)
		if (plan.clearedLocales.length > 0) {
			setSaveError(clearedText(plan.clearedLocales))
			return
		}
		setSaveError(null)
		setIsSaving(true)
		try {
			await runRowWrites(row, source, plan, writers)
			cancelEdit()
		} catch {
			// Writes are independent, so a partial save is possible; the row stays
			// open on its draft rather than closing over an unknown outcome.
			setSaveError('Could not save every change. Some cells may not have been saved — check the row and try again.')
		} finally {
			setIsSaving(false)
		}
	}

	return {
		editingRowId: editor.editingRowId,
		draft: editor.draft,
		startEdit,
		cancelEdit,
		setDraftCell: editor.setCell,
		saveEdit,
		isSaving,
		saveError,
	}
}
